/**
 * =========================================================================================
 *     DOBLER DATA SOLUTIONS, LLC - PROPRIETARY AND CONFIDENTIAL
 * =========================================================================================
 * The source code contained herein is owned by Dobler Data Solutions, LLC. The source code
 * and all other copyrightable materials related thereto, including without limitation, all
 * text, logos, designs, graphics, other files, and other code, is protected under Title 17
 * of the United States Code. Any use, including any commercial use, distribution,
 * republication, or display of the source code or any other copyrightable materials related
 * thereto is strictly prohibited. Copyright © 2026 Dobler Data Solutions, LLC.
 * ALL RIGHTS RESERVED.
 * =========================================================================================
 *
 *     File Name       :   user.ts
 *     Description     :   TanStack Query hooks for the current user + preferences (/api/v1/me) and the
 *                         Administrator user-management surface (/api/v1/users).
 *     Module          :   user-management (frontend/api)
 *     Component Type  :   API
 *
 * -----------------------------------------------------------------------------------------
 *     CHANGE HISTORY
 * -----------------------------------------------------------------------------------------
 *     Version     Date            Author              Description
 * -----------------------------------------------------------------------------------------
 *     1.0         2026-06-25      Peter Dobler        Initial creation (user + role + preferences)
 * -----------------------------------------------------------------------------------------
 *
 *     EXPORTS
 * -----------------------------------------------------------------------------------------
 *     useMeQuery, useUpdatePreferences, useUsers, useUpdateUserRole
 *
 * =========================================================================================
 */

import { useMutation, useQuery, useQueryClient, type UseMutationResult } from "@tanstack/react-query";
import { fetchWithAuth } from "./migrationControl.js";
import { putWithAuth } from "./migrations.js";
import type { AppRole, AppUser, AppUserPreferences } from "@nextgenmigration/shared/user";

const CONTROL_PLANE_URL = (import.meta.env["VITE_CONTROL_PLANE_URL"] as string | undefined) ?? "";

/** Current user (provisioned on first call). Disabled until the caller is authenticated. */
export function useMeQuery(enabled: boolean): ReturnType<typeof useQuery<AppUser>> {
  return useQuery<AppUser>({
    queryKey: ["me"],
    queryFn: () => fetchWithAuth<AppUser>(`${CONTROL_PLANE_URL}/api/v1/me`),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/** Persist the current user's preferences (theme + starting page). */
export function useUpdatePreferences(): UseMutationResult<AppUser, Error, AppUserPreferences> {
  const queryClient = useQueryClient();
  return useMutation<AppUser, Error, AppUserPreferences>({
    mutationFn: (prefs) => putWithAuth<AppUser>(`${CONTROL_PLANE_URL}/api/v1/me/preferences`, prefs),
    onSuccess: (user) => {
      queryClient.setQueryData(["me"], user);
    },
  });
}

/** List all users (Administrator only). */
export function useUsers(enabled: boolean): ReturnType<typeof useQuery<{ items: AppUser[] }>> {
  return useQuery<{ items: AppUser[] }>({
    queryKey: ["users"],
    queryFn: () => fetchWithAuth<{ items: AppUser[] }>(`${CONTROL_PLANE_URL}/api/v1/users`),
    enabled,
  });
}

/** Change a user's role (Administrator only). */
export function useUpdateUserRole(): UseMutationResult<AppUser, Error, { id: number; role: AppRole }> {
  const queryClient = useQueryClient();
  return useMutation<AppUser, Error, { id: number; role: AppRole }>({
    mutationFn: ({ id, role }) =>
      putWithAuth<AppUser>(`${CONTROL_PLANE_URL}/api/v1/users/${String(id)}/role`, { role }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
}
